import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Edit,
  ExternalLink,
  Eye,
  FileSignature,
  Hash,
  Loader2,
  Mail,
  MoreHorizontal,
  MoreVertical,
  Package,
  Plus,
  RefreshCw,
  Search,
  ShoppingCart,
  Trash2,
  Trophy,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";

import { API, formatEST, dataCache, REQUEST_STATUS_OPTIONS, formatLengthForEmail } from "./utils";


function OrderPoleDialog({ open, onClose, poleSpec, requestId, onOrder }) {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleOrder = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/poles/order`, {
        length: poleSpec.length,
        weight: poleSpec.weight,
        brand: 'ESSX',
        notes: notes,
        original_request_id: requestId
      });
      toast.success(`Pole ${poleSpec.length} - ${poleSpec.weight} marked as on order`);
      onOrder();
      onClose();
    } catch (err) {
      toast.error('Failed to order pole');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Order Pole
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="font-semibold text-amber-900">
              {poleSpec?.length} - {poleSpec?.weight}
            </div>
            <div className="text-sm text-amber-700 mt-1">
              This will create a temporary pole in inventory marked as "On Order"
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Expected delivery date, supplier..."
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleOrder} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
            Mark as On Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function generateEmailContent(request, hasAllPoles) {
  const requesterName = request.requester_name?.split(' ')[0] || 'there';
  const baseUrl = window.location.origin;
  const selectFormUrl = `${baseUrl}/select-alternate?request=${request.request_id}`;
  const paymentUrl = `${baseUrl}/add-payment?request=${request.request_id}`;
  const jotformUrl = `${baseUrl}/sign-agreement?request=${request.request_id}`;
  
  if (hasAllPoles) {
    const polesList = request.desired_poles?.filter(dp => dp.exact_available).map(dp => `• ${formatLengthForEmail(dp.requested.length)} – ${dp.requested.weight}`).join('\n') || '';
    return {
      subject: `Pole Rental Confirmation - Request #${request.request_id}`,
      body: `Hi ${requesterName},

Great news! The poles you requested are available:

${polesList}

To complete your rental, please complete these 3 steps (in any order):

1. CONFIRM POLES: ${selectFormUrl}
2. ADD PAYMENT METHOD: ${paymentUrl}
3. SIGN RENTAL AGREEMENT: ${jotformUrl}

Once all 3 steps are completed, your poles will be ready for pickup!

Best regards,
James`
    };
  } else {
    const unavailable = request.desired_poles?.filter(dp => !dp.exact_available) || [];
    
    const alternativesList = unavailable.map(dp => {
      const alts = dp.similar_matches?.slice(0, 3);
      let optionsText;
      
      if (!alts || alts.length === 0) {
        optionsText = 'None available';
      } else {
        optionsText = alts.map(m => `${formatLengthForEmail(m.length)} – ${m.weight}`).join(', ');
      }
      
      return `Requested: ${formatLengthForEmail(dp.requested.length)} – ${dp.requested.weight}
Available Options: ${optionsText}`;
    }).join('\n\n');
    
    return {
      subject: `Pole Rental Update - Request #${request.request_id}`,
      body: `Hi ${requesterName},

Some of the poles you requested are not currently available. Here are the closest alternatives:

${alternativesList}

To proceed with your rental, please complete these 3 steps (in any order):

1. SELECT POLES: ${selectFormUrl}
2. ADD PAYMENT METHOD: ${paymentUrl}
3. SIGN RENTAL AGREEMENT: ${jotformUrl}

Please let me know if these alternatives work for you, or if you'd like me to check additional options.

Best regards,
James`
    };
  }
}

// Pole Request Table Row
// Pole Selection Dialog for creating rental with pole selection
function PoleSelectionDialog({ open, onClose, request, onCreateRental }) {
  const [selectedPoles, setSelectedPoles] = useState({});
  const [creating, setCreating] = useState(false);
  
  // Serial number search state
  const [serialSearch, setSerialSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manualPoles, setManualPoles] = useState([]); // Poles added via serial search

  // Initialize selections when dialog opens
  useEffect(() => {
    if (open && request?.desired_poles) {
      const initial = {};
      request.desired_poles.forEach((dp, idx) => {
        // Pre-select first exact match if available
        if (dp.exact_available && dp.exact_matches?.[0]) {
          initial[idx] = dp.exact_matches[0].pole_id;
        } else if (dp.similar_matches?.[0]) {
          // Pre-select first alternative if no exact match
          initial[idx] = dp.similar_matches[0].pole_id;
        }
      });
      setSelectedPoles(initial);
      setManualPoles([]);
      setSerialSearch('');
      setSearchResults([]);
    }
  }, [open, request]);

  const handlePoleSelect = (dpIndex, poleId) => {
    setSelectedPoles(prev => ({ ...prev, [dpIndex]: poleId }));
  };

  // Search for poles by serial number
  const handleSerialSearch = async () => {
    if (!serialSearch.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/poles/search?q=${encodeURIComponent(serialSearch.trim())}`);
      setSearchResults(response.data.poles || []);
      if (response.data.poles?.length === 0) {
        toast.info('No poles found matching that serial number');
      }
    } catch (err) {
      toast.error('Failed to search poles');
    } finally {
      setSearching(false);
    }
  };

  // Add a pole from search results to manual selection
  const addManualPole = (pole) => {
    if (manualPoles.find(p => p.pole_id === pole.pole_id)) {
      toast.info('Pole already added');
      return;
    }
    setManualPoles(prev => [...prev, pole]);
    setSearchResults([]);
    setSerialSearch('');
    toast.success(`Added ${pole.pole_code || pole.serial_num || 'Pole #' + pole.pole_id}`);
  };

  // Remove a manually added pole
  const removeManualPole = (poleId) => {
    setManualPoles(prev => prev.filter(p => p.pole_id !== poleId));
  };

  const handleCreate = async () => {
    const specPoleIds = Object.values(selectedPoles).filter(id => id);
    const manualPoleIds = manualPoles.map(p => p.pole_id);
    const allPoleIds = [...new Set([...specPoleIds, ...manualPoleIds])]; // Dedupe
    
    if (allPoleIds.length === 0) {
      toast.error("Please select at least one pole");
      return;
    }

    setCreating(true);
    try {
      await onCreateRental(request.request_id, allPoleIds);
      onClose();
    } catch (err) {
      // Error already handled in parent
    } finally {
      setCreating(false);
    }
  };

  const selectedCount = Object.values(selectedPoles).filter(id => id).length + manualPoles.length;
  const totalSpecs = request?.desired_poles?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Rental - Select Poles</DialogTitle>
          <DialogDescription>
            Select poles for each requested specification, or search by serial number to add any pole.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Requester Info */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <div className="font-medium">{request?.requester_name}</div>
            <div className="text-slate-500">{request?.requester_email}</div>
            <div className="text-slate-500 mt-1">
              Request #{request?.request_id} • {request?.desired_poles?.length || 0} pole(s) requested
            </div>
          </div>

          {/* Serial Number Search */}
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search by Serial Number / Pole Code
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter serial number or pole code..."
                value={serialSearch}
                onChange={(e) => setSerialSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSerialSearch()}
                className="flex-1 bg-white"
              />
              <Button 
                variant="outline" 
                onClick={handleSerialSearch}
                disabled={searching || !serialSearch.trim()}
                className="bg-white"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-xs text-blue-700 font-medium">Search Results:</div>
                {searchResults.map(pole => (
                  <div 
                    key={pole.pole_id} 
                    className="flex items-center justify-between p-2 bg-white rounded border border-blue-100 hover:border-blue-300"
                  >
                    <div>
                      <div className="font-medium text-sm">{pole.pole_code || pole.serial_num || `Pole #${pole.pole_id}`}</div>
                      <div className="text-xs text-slate-500">
                        {pole.length} - {pole.weight} • {pole.brand || 'N/A'} 
                        {pole.status && <span className="ml-2 text-slate-400">({pole.status})</span>}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => addManualPole(pole)}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Manually Added Poles */}
          {manualPoles.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
              <div className="text-sm font-medium text-emerald-800 mb-2">
                Manually Added Poles ({manualPoles.length}):
              </div>
              <div className="space-y-2">
                {manualPoles.map(pole => (
                  <div key={pole.pole_id} className="flex items-center justify-between p-2 bg-white rounded border border-emerald-200">
                    <div>
                      <span className="font-medium">{pole.pole_code || pole.serial_num || `Pole #${pole.pole_id}`}</span>
                      <span className="text-sm text-slate-500 ml-2">{pole.length} - {pole.weight}</span>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeManualPole(pole.pole_id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pole Selection for each spec */}
          {request?.desired_poles?.map((dp, dpIdx) => (
            <div key={dpIdx} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-900">
                  {dp.requested.length} - {dp.requested.weight}
                </div>
                {dp.exact_available ? (
                  <Badge className="bg-emerald-500 text-white text-xs">{dp.exact_count} Exact</Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                    {dp.similar_matches?.length || 0} Alternatives
                  </Badge>
                )}
              </div>

              <RadioGroup 
                value={selectedPoles[dpIdx]?.toString() || ''} 
                onValueChange={(val) => handlePoleSelect(dpIdx, parseInt(val))}
              >
                {/* Exact Matches */}
                {dp.exact_matches?.length > 0 && (
                  <div className="space-y-2 mb-2">
                    <div className="text-xs font-medium text-emerald-700">Exact Matches:</div>
                    {dp.exact_matches.map((pole) => (
                      <div key={pole.pole_id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-emerald-50 border border-transparent hover:border-emerald-200">
                        <RadioGroupItem value={pole.pole_id.toString()} id={`pole-${dpIdx}-${pole.pole_id}`} />
                        <Label htmlFor={`pole-${dpIdx}-${pole.pole_id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {pole.length} - {pole.weight}
                              <span className="text-slate-400 font-normal ml-2">
                                SN: {pole.serial_num || 'N/A'}
                              </span>
                            </span>
                            <span className="text-sm text-slate-500">{pole.brand}</span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                )}

                {/* Alternatives */}
                {dp.similar_matches?.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-amber-700">
                      {dp.exact_available ? 'Alternatives:' : 'Available Alternatives:'}
                    </div>
                    {dp.similar_matches.map((pole) => (
                      <div key={pole.pole_id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-amber-50 border border-transparent hover:border-amber-200">
                        <RadioGroupItem value={pole.pole_id.toString()} id={`pole-${dpIdx}-${pole.pole_id}`} />
                        <Label htmlFor={`pole-${dpIdx}-${pole.pole_id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {pole.length} - {pole.weight}
                              <span className="text-slate-400 font-normal ml-2">
                                SN: {pole.serial_num || 'N/A'}
                              </span>
                            </span>
                            <span className="text-sm text-slate-500">{pole.brand}</span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                )}

                {/* No options available */}
                {!dp.exact_matches?.length && !dp.similar_matches?.length && (
                  <div className="text-sm text-red-500 p-2">No poles available for this specification</div>
                )}

                {/* Skip option */}
                <div className="flex items-center space-x-3 p-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 mt-2">
                  <RadioGroupItem value="" id={`pole-${dpIdx}-skip`} />
                  <Label htmlFor={`pole-${dpIdx}-skip`} className="flex-1 cursor-pointer text-slate-500">
                    Skip this specification
                  </Label>
                </div>
              </RadioGroup>
            </div>
          ))}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-slate-500">
              {selectedCount} pole(s) selected {manualPoles.length > 0 && `(${manualPoles.length} manual)`}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating || selectedCount === 0}>
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Rental
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PoleRequestTableRow({ request, onStatusChange, onSendEmail, onOrderPole, onUserClick, onCreateRental, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [showPoleSelection, setShowPoleSelection] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const hasAllPoles = request.desired_poles?.every(dp => dp.exact_available);
  const hasNoPoles = request.desired_poles?.every(dp => !dp.exact_available && !dp.similar_matches?.length);
  const hasSomePoles = !hasAllPoles && !hasNoPoles;
  const hasAnyOptions = request.desired_poles?.some(dp => dp.exact_available || dp.similar_matches?.length > 0);
  const workflow = request.workflow;
  
  // Get JotForm status for this specific request
  const getJotformStatusForRequest = () => {
    if (!workflow) return { status: 'none', label: 'Unsigned', color: 'slate' };
    const jotformStatus = workflow.jotform_status || 'pending';
    const signedAt = workflow.jotform_signed_at;
    if (jotformStatus === 'completed') {
      return { status: 'completed', label: signedAt ? `Signed ${signedAt}` : 'Signed', color: 'emerald' };
    }
    if (jotformStatus === 'opened') {
      return { status: 'opened', label: 'Form Opened', color: 'blue' };
    }
    return { status: 'pending', label: 'Unsigned', color: 'slate' };
  };
  
  // Get Stripe/Payment status
  const getPaymentStatus = () => {
    if (request.has_payment_method) {
      return { status: 'on_file', label: 'On File', color: 'emerald' };
    }
    if (!workflow) return { status: 'none', label: 'No Payment', color: 'red' };
    const stripeStatus = workflow.stripe_status || 'pending';
    if (stripeStatus === 'completed') return { status: 'completed', label: 'Added', color: 'emerald' };
    if (stripeStatus === 'opened') return { status: 'opened', label: 'In Progress', color: 'blue' };
    if (stripeStatus === 'skipped') return { status: 'skipped', label: 'Skipped', color: 'amber' };
    return { status: 'pending', label: 'No Payment', color: 'red' };
  };

  const jotformInfo = getJotformStatusForRequest();
  const paymentInfo = getPaymentStatus();
  
  const getAvailabilityBadge = () => {
    if (hasAllPoles) return <Badge className="bg-emerald-500 text-white text-xs"><Check className="w-3 h-3 mr-1" />Available</Badge>;
    if (hasNoPoles) return <Badge variant="destructive" className="text-xs"><X className="w-3 h-3 mr-1" />None</Badge>;
    return <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs"><AlertTriangle className="w-3 h-3 mr-1" />Partial</Badge>;
  };

  const polesCount = request.desired_poles?.length || 0;

  // Check if rental already exists
  const hasExistingRental = request.rental?.id;
  
  return (
    <>
      {/* Pole Selection Dialog */}
      <PoleSelectionDialog
        open={showPoleSelection}
        onClose={() => setShowPoleSelection(false)}
        request={request}
        onCreateRental={onCreateRental}
      />
      
      <TableRow 
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`pole-request-row-${request.request_id}`}
      >
        {/* Expand Icon */}
        <TableCell className="w-10 p-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </TableCell>
        
        {/* Request ID */}
        <TableCell className="font-mono text-sm w-20">
          <div>#{request.request_id}</div>
          {request.rental?.id && (
            <div 
              className="flex items-center gap-1 mt-0.5 cursor-pointer hover:opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/rentals/${request.rental.id}`, e.metaKey || e.ctrlKey ? '_blank' : '_self');
              }}
            >
              <span className="text-[10px] text-blue-600 hover:underline">R#{request.rental.id}</span>
              {request.rental.status && (
                <Badge className="text-[9px] px-1 py-0 h-4 leading-none bg-emerald-100 text-emerald-700">{request.rental.status}</Badge>
              )}
            </div>
          )}
        </TableCell>
        
        {/* Requester */}
        <TableCell>
          <div 
            className="hover:text-blue-600 cursor-pointer"
            onClick={(e) => { 
              e.stopPropagation(); 
              if (e.metaKey || e.ctrlKey) {
                window.open(`/users/${request.user_id}`, '_blank');
              } else {
                onUserClick && onUserClick(request.user_id, request.requester_name);
              }
            }}
          >
            <div className="font-medium text-sm flex items-center gap-1.5">
              {request.requester_name}
              {request.request_type === 'meet' && (
                <Badge data-testid={`meet-badge-${request.request_id}`} className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 h-4 leading-none">MEET</Badge>
              )}
              {request.request_type === 'rental_request' && (
                <Badge data-testid={`rental-request-badge-${request.request_id}`} className="bg-blue-100 text-blue-800 text-[9px] px-1.5 py-0 h-4 leading-none">SELF-SERVE</Badge>
              )}
            </div>
            <div className="text-xs text-slate-500">{request.requester_email}</div>
          </div>
        </TableCell>
        
        {/* Status */}
        <TableCell onClick={e => e.stopPropagation()}>
          <Select value={['requested', 'confirmation pending', 'alternate_selected', 'approved', 'awaiting pickup', 'completed', 'cancelled'].includes(request.status) ? request.status : 'requested'} onValueChange={(value) => onStatusChange(request.request_id, value)}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REQUEST_STATUS_OPTIONS.filter(s => s !== 'all').map(status => (
                <SelectItem key={status} value={status} className="text-xs">{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        
        {/* Poles */}
        <TableCell className="text-center">
          <span className="text-sm font-medium">{polesCount}</span>
        </TableCell>
        
        {/* Dates */}
        <TableCell className="text-xs">
          <div>{request.rental_start_date ? formatEST(request.rental_start_date, 'MMM d') : 'TBD'}</div>
          <div className="text-slate-400">{request.rental_return_date ? `→ ${formatEST(request.rental_return_date, 'MMM d')}` : ''}</div>
        </TableCell>
        
        {/* Notes */}
        <TableCell className="max-w-[150px]">
          {request.notes ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-slate-600 truncate block cursor-help">
                  {request.notes}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[300px] whitespace-pre-wrap bg-slate-900 text-white">
                {request.notes}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-xs text-slate-300">-</span>
          )}
        </TableCell>
        
        {/* JotForm */}
        <TableCell>
          <div className={`flex items-center gap-1 text-xs ${
            jotformInfo.color === 'emerald' ? 'text-emerald-600' : 
            jotformInfo.color === 'blue' ? 'text-blue-600' : 'text-slate-400'
          }`}>
            {jotformInfo.status === 'completed' && <Check className="w-3 h-3" />}
            <span>{jotformInfo.label}</span>
            {jotformInfo.status === 'completed' && request.workflow?.jotform_submission_id && (
              <a 
                href={`https://www.jotform.com/submission/${request.workflow.jotform_submission_id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-1 text-blue-500 hover:text-blue-700"
                title="View signed form"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </TableCell>
        
        {/* Payment */}
        <TableCell>
          <div className={`flex items-center gap-1 text-xs ${
            paymentInfo.color === 'emerald' ? 'text-emerald-600' : 
            paymentInfo.color === 'red' ? 'text-red-500' : 
            paymentInfo.color === 'blue' ? 'text-blue-600' : 'text-amber-600'
          }`}>
            {(paymentInfo.status === 'on_file' || paymentInfo.status === 'completed') && <Check className="w-3 h-3" />}
            <span>{paymentInfo.label}</span>
          </div>
        </TableCell>
        
        {/* Availability */}
        <TableCell>{getAvailabilityBadge()}</TableCell>
        
        {/* Created Date */}
        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
          {request.created_at ? formatEST(request.created_at, 'MMM d, yyyy h:mm a') : '-'}
        </TableCell>
        
        {/* Actions */}
        <TableCell onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onSendEmail(request)} 
              className="gap-1 text-xs h-7"
            >
              <Mail className="w-3 h-3" />Email
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => setShowPoleSelection(true)}
                  disabled={!hasAnyOptions || hasExistingRental}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Rental & Approve</span>
                </DropdownMenuItem>
                {hasExistingRental && (
                  <div className="px-2 py-1 text-xs text-slate-500">
                    Already linked to Rental #{request.rental.id}
                  </div>
                )}
                {!hasAnyOptions && (
                  <div className="px-2 py-1 text-xs text-red-500">
                    No poles available
                  </div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onUserClick && onUserClick(request.user_id, request.requester_name)}
                  className="gap-2"
                >
                  <User className="w-4 h-4" />
                  <span>View Customer</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setShowEditDialog(true)}
                  className="gap-2"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Request</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  data-testid="copy-agreement-url-btn"
                  onClick={async () => {
                    try {
                      const agreementUrl = `${window.location.origin}/rental-agreement?request=${request.request_id}`;
                      try {
                        await navigator.clipboard.writeText(agreementUrl);
                        toast.success("Agreement URL copied to clipboard!");
                      } catch (clipErr) {
                        toast.success(
                          <div className="text-xs">
                            <p className="font-medium mb-1">Agreement URL:</p>
                            <p className="break-all select-all">{agreementUrl}</p>
                          </div>,
                          { duration: 15000 }
                        );
                      }
                    } catch (e) {
                      toast.error(e.message || "Failed to generate agreement URL");
                    }
                  }}
                  className="gap-2"
                >
                  <FileSignature className="w-4 h-4" />
                  <span>Copy Agreement URL</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`download-signed-contract-btn-${request.request_id}`}
                  onClick={async () => {
                    try {
                      // First check if a signed contract exists
                      const status = await axios.get(`${API}/contracts/${request.request_id}/status`);
                      if (!status.data?.signed) {
                        toast.error("No signed contract on file for this request yet.");
                        return;
                      }
                      // Open the PDF in a new tab
                      const pdfUrl = `${API}/contracts/${request.request_id}/pdf`;
                      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || e.message || "Failed to open contract PDF");
                    }
                  }}
                  className="gap-2"
                >
                  <FileSignature className="w-4 h-4" />
                  <span>View Signed Contract (PDF)</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/pole-requests/${request.request_id}/jotform-url`);
                      if (response.data.success && response.data.url) {
                        try {
                          await navigator.clipboard.writeText(response.data.url);
                          toast.success("JotForm link copied to clipboard!");
                        } catch (clipErr) {
                          // Clipboard failed, show the link in toast
                          console.log("Clipboard error:", clipErr);
                          toast.success(
                            <div className="text-xs">
                              <p className="font-medium mb-1">JotForm Link:</p>
                              <p className="break-all select-all">{response.data.url}</p>
                            </div>,
                            { duration: 15000 }
                          );
                        }
                      } else {
                        toast.error("No URL returned from server");
                      }
                    } catch (e) {
                      console.error("JotForm URL error:", e);
                      toast.error(e.response?.data?.detail || e.message || "Failed to generate JotForm link");
                    }
                  }}
                  className="gap-2"
                >
                  <FileSignature className="w-4 h-4" />
                  <span>Copy JotForm Link (legacy)</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={async () => {
                    try {
                      const response = await axios.post(`${API}/users/${request.user_id}/payment-method-link`);
                      if (response.data.success && response.data.url) {
                        await navigator.clipboard.writeText(response.data.url);
                        toast.success("Payment link copied to clipboard!");
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to generate payment link");
                    }
                  }}
                  className="gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Copy Payment Link</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
      
      {/* Expanded Row */}
      {expanded && (
        <TableRow className="bg-slate-50">
          <TableCell colSpan={10} className="p-0">
            <div className="p-4 border-t border-slate-200">
              {request.notes && (
                <div className="mb-4 text-xs text-slate-600 bg-white p-3 rounded border border-slate-200">
                  <span className="font-medium">Notes:</span> {request.notes}
                </div>
              )}
              
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Requested Poles ({polesCount})</h4>
              
              {polesCount === 0 ? (
                <div className="text-xs text-slate-400">No specific poles requested</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {request.desired_poles?.map((dp, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-slate-900 text-sm">{dp.requested.length} - {dp.requested.weight}</div>
                        {dp.exact_available ? (
                          <Badge className="bg-emerald-500 text-xs">{dp.exact_count} Avail</Badge>
                        ) : (
                          <div className="flex gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 text-xs px-2 gap-1 text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={(e) => { e.stopPropagation(); onOrderPole(dp.requested, request.request_id); }}
                            >
                              <ShoppingCart className="w-3 h-3" />Order
                            </Button>
                            <Badge variant="destructive" className="text-xs">N/A</Badge>
                          </div>
                        )}
                      </div>
                      
                      {dp.exact_matches?.length > 0 && (
                        <div className="mb-2">
                          <div className="text-xs text-emerald-700 mb-1">Exact: {dp.exact_matches.map(p => p.pole_code || `#${p.pole_id}`).join(', ')}</div>
                        </div>
                      )}
                      
                      {!dp.exact_available && dp.similar_matches?.length > 0 && (
                        <div className="text-xs text-amber-700">
                          Alts: {dp.similar_matches.slice(0, 3).map(p => `${p.length}-${p.weight}`).join(', ')}
                        </div>
                      )}
                      
                      {!dp.exact_available && dp.similar_matches?.length === 0 && (
                        <div className="text-xs text-red-500">No alternatives</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {request.rental && (
                <div className="mt-3 text-xs text-blue-600">
                  <span className="font-medium">Linked to Rental #{request.rental.id}</span>
                  {request.rental.status && <Badge className="ml-2 text-xs">{request.rental.status}</Badge>}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
      
      {/* Edit Pole Request Dialog */}
      <EditPoleRequestDialog 
        open={showEditDialog} 
        onClose={() => setShowEditDialog(false)} 
        request={request}
        onUpdated={onEdit}
      />
    </>
  );
}

// Edit Pole Request Dialog
function EditPoleRequestDialog({ open, onClose, request, onUpdated }) {
  const [startDate, setStartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [notes, setNotes] = useState("");
  const [insurance, setInsurance] = useState(false);
  const [poles, setPoles] = useState([{ length: "", weight: "" }]);
  const [saving, setSaving] = useState(false);
  const [poleSizes, setPoleSizes] = useState({ lengths: [], weights: [] });

  // Load data when dialog opens
  useEffect(() => {
    if (open && request) {
      setStartDate(request.rental_start_date ? request.rental_start_date.split('T')[0] : "");
      setReturnDate(request.rental_return_date ? request.rental_return_date.split('T')[0] : "");
      setNotes(request.notes || "");
      setInsurance(request.insurance || false);
      // Load existing poles
      if (request.desired_poles && request.desired_poles.length > 0) {
        setPoles(request.desired_poles.map(dp => ({
          length: dp.requested?.length || dp.length || "",
          weight: dp.requested?.weight || dp.weight || ""
        })));
      } else {
        setPoles([{ length: "", weight: "" }]);
      }
      fetchPoleSizes();
    }
  }, [open, request]);

  const fetchPoleSizes = async () => {
    try {
      const response = await axios.get(`${API}/poles/sizes`);
      setPoleSizes(response.data || { lengths: [], weights: [] });
    } catch (err) {
      console.error("Failed to fetch pole sizes:", err);
    }
  };

  const addPole = () => setPoles([...poles, { length: "", weight: "" }]);
  
  const removePole = (index) => {
    if (poles.length > 1) {
      setPoles(poles.filter((_, i) => i !== index));
    }
  };

  const updatePole = (index, field, value) => {
    const updated = [...poles];
    updated[index][field] = value;
    setPoles(updated);
  };

  const handleSave = async () => {
    const validPoles = poles.filter(p => p.length && p.weight);
    if (validPoles.length === 0) {
      toast.error("Please add at least one pole with length and weight");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/pole-requests/${request.request_id}`, {
        rental_start_date: startDate || null,
        rental_return_date: returnDate || null,
        notes: notes || null,
        insurance,
        poles: validPoles
      });
      toast.success("Pole request updated successfully");
      onClose();
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pole request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Pole Request #{request?.request_id}</DialogTitle>
          <DialogDescription>
            Update the pole request details for {request?.requester_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />
            </div>
            <div>
              <Label>Return Date</Label>
              <Input 
                type="date" 
                value={returnDate} 
                onChange={(e) => setReturnDate(e.target.value)} 
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Input 
              value={notes} 
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          {/* Insurance */}
          <div className="flex items-center gap-2">
            <Checkbox 
              id="edit-insurance"
              checked={insurance} 
              onCheckedChange={setInsurance} 
            />
            <Label htmlFor="edit-insurance" className="cursor-pointer">Include Insurance</Label>
          </div>

          {/* Poles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Requested Poles</Label>
              <Button type="button" variant="outline" size="sm" onClick={addPole} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" />Add Pole
              </Button>
            </div>
            
            <div className="space-y-2">
              {poles.map((pole, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select value={pole.length} onValueChange={(v) => updatePole(index, 'length', v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Length" />
                    </SelectTrigger>
                    <SelectContent>
                      {poleSizes.lengths.map(len => (
                        <SelectItem key={len} value={len}>{len}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={pole.weight} onValueChange={(v) => updatePole(index, 'weight', v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Weight" />
                    </SelectTrigger>
                    <SelectContent>
                      {poleSizes.weights.map(w => (
                        <SelectItem key={w} value={w}>{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {poles.length > 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removePole(index)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Pole Requests Tab
// Create Pole Request Dialog
function CreatePoleRequestDialog({ open, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [notes, setNotes] = useState("");
  const [insurance, setInsurance] = useState(false);
  const [poles, setPoles] = useState([{ length: "", weight: "" }]);
  const [creating, setCreating] = useState(false);
  const [poleSizes, setPoleSizes] = useState({ lengths: [], weights: [] });
  const [lengthSearches, setLengthSearches] = useState({});
  const [weightSearches, setWeightSearches] = useState({});
  const [openLengthPopover, setOpenLengthPopover] = useState({});
  const [openWeightPopover, setOpenWeightPopover] = useState({});

  // Fetch users and pole sizes when dialog opens
  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchPoleSizes();
    }
  }, [open]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users/all`);
      setUsers(response.data.users || []);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchPoleSizes = async () => {
    try {
      const response = await axios.get(`${API}/poles/sizes`);
      setPoleSizes(response.data || { lengths: [], weights: [] });
    } catch (err) {
      console.error("Failed to fetch pole sizes:", err);
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFilteredLengths = (index) => {
    const search = lengthSearches[index] || "";
    return poleSizes.lengths.filter(l => 
      l.toLowerCase().includes(search.toLowerCase())
    );
  };

  const getFilteredWeights = (index) => {
    const search = weightSearches[index] || "";
    return poleSizes.weights.filter(w => 
      w.toString().toLowerCase().includes(search.toLowerCase())
    );
  };

  const addPole = () => {
    setPoles([...poles, { length: "", weight: "" }]);
  };

  const removePole = (index) => {
    if (poles.length > 1) {
      setPoles(poles.filter((_, i) => i !== index));
      // Clean up search states
      const newLengthSearches = { ...lengthSearches };
      const newWeightSearches = { ...weightSearches };
      delete newLengthSearches[index];
      delete newWeightSearches[index];
      setLengthSearches(newLengthSearches);
      setWeightSearches(newWeightSearches);
    }
  };

  const updatePole = (index, field, value) => {
    const updated = [...poles];
    updated[index][field] = value;
    setPoles(updated);
  };

  const handleCreate = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    if (!startDate) {
      toast.error("Please set a start date");
      return;
    }
    const validPoles = poles.filter(p => p.length && p.weight);
    if (validPoles.length === 0) {
      toast.error("Please add at least one pole with length and weight");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/pole-requests/create`, {
        user_id: selectedUser.id,
        rental_start_date: startDate,
        rental_return_date: returnDate || null,
        notes: notes || undefined,
        insurance: insurance,
        poles: validPoles
      });

      toast.success(`Request #${response.data.request_id} created for ${response.data.user_name}`);
      
      // Reset form
      setSelectedUser(null);
      setStartDate("");
      setReturnDate("");
      setNotes("");
      setInsurance(false);
      setPoles([{ length: "", weight: "" }]);
      setSearchQuery("");
      setLengthSearches({});
      setWeightSearches({});
      
      onClose();
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create request");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Pole Request</DialogTitle>
          <DialogDescription>Manually create a new pole rental request</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Selection */}
          <div className="space-y-2">
            <Label>Select User *</Label>
            {selectedUser ? (
              <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div>
                  <div className="font-medium">{selectedUser.full_name}</div>
                  <div className="text-sm text-slate-500">{selectedUser.email}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <ScrollArea className="h-40 border border-slate-200 rounded-lg">
                  {loadingUsers ? (
                    <div className="p-4 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">No users found</div>
                  ) : (
                    <div className="p-1">
                      {filteredUsers.slice(0, 20).map(user => (
                        <div
                          key={user.id}
                          onClick={() => setSelectedUser(user)}
                          className="p-2 hover:bg-slate-100 rounded cursor-pointer"
                        >
                          <div className="font-medium text-sm">{user.full_name}</div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Return Date <span className="text-slate-400 text-xs">(Optional)</span></Label>
              <Input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                data-testid="return-date-input"
              />
            </div>
          </div>

          {/* Poles */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Requested Poles *</Label>
              <Button variant="outline" size="sm" onClick={addPole} data-testid="add-pole-btn">
                <Plus className="w-3 h-3 mr-1" />Add Pole
              </Button>
            </div>
            <div className="space-y-3">
              {poles.map((pole, index) => (
                <div key={index} className="flex items-start gap-2" data-testid={`pole-row-${index}`}>
                  {/* Length Combobox */}
                  <div className="flex-1">
                    <Popover 
                      open={openLengthPopover[index]} 
                      onOpenChange={(open) => setOpenLengthPopover({ ...openLengthPopover, [index]: open })}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openLengthPopover[index]}
                          className="w-full justify-between font-normal"
                          data-testid={`length-select-${index}`}
                        >
                          {pole.length || "Select Length"}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[200px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search lengths..." data-testid={`length-search-${index}`} />
                          <CommandList>
                            <CommandEmpty>No length found.</CommandEmpty>
                            <CommandGroup>
                              {poleSizes.lengths.map((length) => (
                                <CommandItem
                                  key={length}
                                  value={length}
                                  onSelect={(value) => {
                                    updatePole(index, 'length', value);
                                    setOpenLengthPopover({ ...openLengthPopover, [index]: false });
                                  }}
                                  data-testid={`length-option-${length}`}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${pole.length === length ? "opacity-100" : "opacity-0"}`} />
                                  {length}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* Weight Combobox */}
                  <div className="flex-1">
                    <Popover 
                      open={openWeightPopover[index]} 
                      onOpenChange={(open) => setOpenWeightPopover({ ...openWeightPopover, [index]: open })}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openWeightPopover[index]}
                          className="w-full justify-between font-normal"
                          data-testid={`weight-select-${index}`}
                        >
                          {pole.weight ? `${pole.weight} lbs` : "Select Weight"}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[200px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search weights..." data-testid={`weight-search-${index}`} />
                          <CommandList>
                            <CommandEmpty>No weight found.</CommandEmpty>
                            <CommandGroup>
                              {poleSizes.weights.map((weight) => (
                                <CommandItem
                                  key={weight}
                                  value={weight}
                                  onSelect={(value) => {
                                    updatePole(index, 'weight', value);
                                    setOpenWeightPopover({ ...openWeightPopover, [index]: false });
                                  }}
                                  data-testid={`weight-option-${weight}`}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${pole.weight === weight ? "opacity-100" : "opacity-0"}`} />
                                  {weight} lbs
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {poles.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removePole(index)} className="mt-0.5" data-testid={`remove-pole-${index}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input
              placeholder="Optional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Insurance */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="insurance"
              checked={insurance}
              onChange={(e) => setInsurance(e.target.checked)}
              className="rounded border-slate-300"
            />
            <Label htmlFor="insurance" className="cursor-pointer">Include insurance ($10/pole/month)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PoleRequestsTab({ onUserClick }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("todo"); // 'all' or 'todo'
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderPoleSpec, setOrderPoleSpec] = useState(null);
  const [orderRequestId, setOrderRequestId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Format time to EST
  const formatToEST = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ' EST';
    } catch (e) {
      return null;
    }
  };

  // Fetch last sync time
  const fetchLastSyncTime = async () => {
    try {
      const response = await axios.get(`${API}/jotform/last-sync`);
      if (response.data.last_sync) {
        setLastSyncTime(response.data.last_sync);
      }
    } catch (err) {
      console.error("Failed to fetch last sync time:", err);
    }
  };

  const fetchRequests = async (forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = dataCache.get('pole_requests');
      if (cached) {
        setRequests(cached);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pole-requests/pending`);
      const data = response.data.requests || [];
      setRequests(data);
      dataCache.set('pole_requests', data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error("Failed to load pole requests");
    } finally {
      setLoading(false);
    }
  };

  const syncSignatures = async (silent = false) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/jotform/sync`);
      const data = response.data;
      if (data.last_sync) {
        setLastSyncTime(data.last_sync);
      }
      if (data.updated_count > 0) {
        if (!silent) toast.success(`Synced ${data.updated_count} signature(s) from JotForm`);
        dataCache.invalidate('pole_requests');
        fetchRequests(true); // Force refresh the list
      } else {
        if (!silent) toast.info(`No new signatures to sync (${data.total_submissions} submissions checked)`);
      }
    } catch (err) {
      if (!silent) toast.error("Failed to sync signatures");
    } finally {
      setSyncing(false);
    }
  };

  // Initial fetch and auto-sync setup
  useEffect(() => { 
    fetchRequests(); 
    fetchLastSyncTime();
    
    // Auto-sync JotForm every hour (3600000 ms)
    const autoSyncInterval = setInterval(() => {
      console.log('Auto-syncing JotForm signatures...');
      syncSignatures(true); // Silent sync
    }, 3600000); // 1 hour
    
    return () => clearInterval(autoSyncInterval);
  }, []);

  const handleStatusChange = async (requestId, newStatus) => {
    // Optimistically update UI
    const oldRequests = [...requests];
    setRequests(prev => prev.map(r => r.request_id === requestId ? { ...r, status: newStatus } : r));
    
    try {
      await axios.put(`${API}/pole-requests/${requestId}/status`, {
        request_id: requestId,
        status: newStatus
      });
      toast.success(`Status updated to "${newStatus}" for Request #${requestId}`);
    } catch (err) {
      // Revert on error
      setRequests(oldRequests);
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const handleSendEmail = (request) => {
    const hasAllPoles = request.desired_poles?.every(dp => dp.exact_available);
    const { subject, body } = generateEmailContent(request, hasAllPoles);
    window.location.href = `mailto:${request.requester_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleOrderPole = (poleSpec, requestId) => {
    setOrderPoleSpec(poleSpec);
    setOrderRequestId(requestId);
    setOrderDialogOpen(true);
  };

  const handleCreateRental = async (requestId, poleIds) => {
    try {
      const response = await axios.post(`${API}/pole-requests/${requestId}/create-rental`, {
        request_id: requestId,
        pole_ids: poleIds
      });
      
      const data = response.data;
      const poleCodes = data.pole_codes?.join(', ') || `${data.poles_assigned} pole(s)`;
      toast.success(`Rental #${data.rental_id} created with: ${poleCodes}`);
      
      // Invalidate all caches and refresh
      dataCache.invalidateAll();
      fetchRequests(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create rental");
      throw err; // Re-throw so the row can handle the loading state
    }
  };

  // Filter requests based on active tab and search
  const filteredRequests = useMemo(() => {
    let filtered = requests;
    
    // Filter by tab - "To Do" includes all active work statuses
    // Only "completed", "cancelled", and "approved" move to "All"
    if (activeTab === "todo") {
      const todoStatuses = [
        'requested', 
        'confirmation pending', 
        'awaiting pickup',
        'alternate_submitted',
        'alternate_selected',
        'awaiting_signature',
        'signature_pending',
        'jotform_pending',
        'pending'
      ];
      filtered = filtered.filter(r => 
        !r.status || // null/empty status stays in To Do
        todoStatuses.includes(r.status?.toLowerCase())
      );
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(r => {
        const searchFields = [
          r.requester_name,
          r.requester_email,
          String(r.request_id),
          r.status,
          r.notes,
          ...(r.desired_poles || []).map(dp => `${dp.length} ${dp.weight}`)
        ];
        return searchFields.some(field => (field || '').toLowerCase().includes(searchLower));
      });
    }
    
    // Sort: awaiting pickup at the bottom
    filtered.sort((a, b) => {
      const aIsAwaiting = a.status?.toLowerCase() === 'awaiting pickup' ? 1 : 0;
      const bIsAwaiting = b.status?.toLowerCase() === 'awaiting pickup' ? 1 : 0;
      if (aIsAwaiting !== bIsAwaiting) return aIsAwaiting - bIsAwaiting;
      return (b.request_id || 0) - (a.request_id || 0);
    });

    return filtered;
  }, [requests, activeTab, searchTerm]);

  // Count for each tab - includes all active work statuses
  const todoCount = useMemo(() => {
    const todoStatuses = [
      'requested', 
      'confirmation pending', 
      'awaiting pickup',
      'alternate_submitted',
      'alternate_selected',
      'awaiting_signature',
      'signature_pending',
      'jotform_pending',
      'pending'
    ];
    return requests.filter(r => 
      !r.status || 
      todoStatuses.includes(r.status?.toLowerCase())
    ).length;
  }, [requests]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-slate-600">{error}</p>
        <Button onClick={fetchRequests} variant="outline" size="sm" className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <OrderPoleDialog 
        open={orderDialogOpen} 
        onClose={() => setOrderDialogOpen(false)} 
        poleSpec={orderPoleSpec}
        requestId={orderRequestId}
        onOrder={fetchRequests}
      />
      
      {/* Header with Tabs - Mobile responsive */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <h2 className="text-base sm:text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Pole Requests
            </h2>
            
            {/* Tab Buttons */}
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('todo')}
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'todo' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-todo"
              >
                To Do
                {todoCount > 0 && (
                  <span className={`ml-1 sm:ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                    activeTab === 'todo' ? 'bg-red-100 text-red-700' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {todoCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'all' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-all"
              >
                All
                <span className={`ml-1 sm:ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === 'all' ? 'bg-slate-200 text-slate-700' : 'bg-slate-200 text-slate-600'
                }`}>
                  {requests.length}
                </span>
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              data-testid="copy-meet-rental-link-btn"
              variant="outline"
              size="sm"
              onClick={async () => {
                const url = `${window.location.origin}/meet-rental`;
                // Open the form in a new tab so the admin can preview it
                window.open(url, '_blank', 'noopener,noreferrer');
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Meet Rental link copied & opened in new tab!");
                } catch {
                  toast.success(
                    <div className="text-xs">
                      <p className="font-medium mb-1">Meet Rental link (opened in new tab):</p>
                      <p className="break-all select-all">{url}</p>
                    </div>,
                    { duration: 15000 }
                  );
                }
              }}
              className="h-8 text-xs sm:text-sm gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Copy Meet Rental Link</span>
              <span className="sm:hidden">Meet Link</span>
            </Button>
            <Button
              data-testid="copy-rental-request-link-btn"
              variant="outline"
              size="sm"
              onClick={async () => {
                const url = `${window.location.origin}/rental-request`;
                window.open(url, '_blank', 'noopener,noreferrer');
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Rental Request link copied & opened in new tab!");
                } catch {
                  toast.success(
                    <div className="text-xs">
                      <p className="font-medium mb-1">Rental Request link (opened in new tab):</p>
                      <p className="break-all select-all">{url}</p>
                    </div>,
                    { duration: 15000 }
                  );
                }
              }}
              className="h-8 text-xs sm:text-sm gap-2 border-blue-300 text-blue-800 hover:bg-blue-50"
            >
              <FileSignature className="w-4 h-4" />
              <span className="hidden sm:inline">Copy Rental Request Link</span>
              <span className="sm:hidden">Rental Link</span>
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => setShowCreateDialog(true)}
              className="hidden sm:flex h-8 text-xs sm:text-sm gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Request
          </Button>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => syncSignatures(false)}
              disabled={syncing}
              className="gap-2 h-8 text-xs sm:text-sm"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
              <span className="hidden sm:inline">Sync Signatures</span>
            </Button>
            {lastSyncTime && (
              <span className="hidden md:inline text-xs text-slate-500">
                Last: {formatToEST(lastSyncTime)}
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchRequests(true)} className="h-8 w-8 sm:w-auto p-0 sm:px-3">
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          </div>
        </div>
        
        {/* Search Input - Full width on mobile */}
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 w-full sm:w-[250px] text-sm h-9"
            data-testid="pole-requests-search"
          />
        </div>
      </div>

      {/* Create Request Dialog */}
      <CreatePoleRequestDialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)}
        onCreated={fetchRequests}
      />

      {/* Request Cards/Table */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          {activeTab === 'todo' ? 'No pending requests to action' : 'No pole requests found'}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {filteredRequests.map(request => {
              const hasAllPoles = request.desired_poles?.every(dp => dp.exact_available);
              const hasNoPoles = request.desired_poles?.every(dp => !dp.exact_available && !dp.similar_matches?.length);
              const polesCount = request.desired_poles?.length || 0;
              const workflow = request.workflow;
              
              // Get JotForm status
              const getJotformStatus = () => {
                if (!workflow) return { status: 'none', label: 'Unsigned', color: 'slate' };
                const jotformStatus = workflow.jotform_status || 'pending';
                const signedAt = workflow.jotform_signed_at;
                if (jotformStatus === 'completed') {
                  return { status: 'completed', label: signedAt ? `Signed` : 'Signed', color: 'emerald' };
                }
                if (jotformStatus === 'opened') {
                  return { status: 'opened', label: 'Opened', color: 'blue' };
                }
                return { status: 'pending', label: 'Unsigned', color: 'slate' };
              };
              const jotformInfo = getJotformStatus();
              
              const getStatusColor = (status) => {
                switch(status?.toLowerCase()) {
                  case 'requested': return 'bg-blue-100 text-blue-700';
                  case 'confirmation pending': return 'bg-amber-100 text-amber-700';
                  case 'approved': return 'bg-emerald-100 text-emerald-700';
                  case 'completed': return 'bg-slate-100 text-slate-700';
                  case 'awaiting pickup': return 'bg-amber-100 text-amber-800';
                  case 'cancelled': return 'bg-red-100 text-red-700';
                  case 'alternate_selected': return 'bg-purple-100 text-purple-700';
                  case 'alternate_submitted': return 'bg-purple-100 text-purple-700';
                  default: return 'bg-slate-100 text-slate-700';
                }
              };
              
              return (
                <div 
                  key={request.request_id} 
                  className="bg-white border border-slate-200 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => onUserClick && onUserClick(request.user_id, request.requester_name)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">#{request.request_id}</span>
                        <Badge className={`${getStatusColor(request.status)} text-xs`}>
                          {request.status}
                        </Badge>
                      </div>
                      <div className="font-medium text-sm mt-1">{request.requester_name}</div>
                      <div className="text-xs text-slate-500">{request.requester_email}</div>
                    </div>
                    <div className="text-right">
                      {hasAllPoles ? (
                        <Badge className="bg-emerald-500 text-white text-xs"><Check className="w-3 h-3" /></Badge>
                      ) : hasNoPoles ? (
                        <Badge variant="destructive" className="text-xs"><X className="w-3 h-3" /></Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                          <AlertTriangle className="w-3 h-3" />
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Poles requested */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {request.desired_poles?.slice(0, 3).map((dp, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs font-mono">
                        {dp.requested?.length} - {dp.requested?.weight}
                      </Badge>
                    ))}
                    {polesCount > 3 && (
                      <Badge variant="outline" className="text-xs">+{polesCount - 3}</Badge>
                    )}
                  </div>

                  {/* Linked Rental */}
                  {request.rental?.id && (
                    <div 
                      className="flex items-center gap-1.5 mb-2 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/rentals/${request.rental.id}`, e.metaKey || e.ctrlKey ? '_blank' : '_self');
                      }}
                    >
                      <span className="text-xs text-blue-600 font-medium hover:underline">Rental #{request.rental.id}</span>
                      {request.rental.status && (
                        <Badge className="text-[10px] px-1.5 py-0 h-4 leading-none bg-emerald-100 text-emerald-700">{request.rental.status}</Badge>
                      )}
                    </div>
                  )}
                  
                  {/* Date and payment info */}
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <span>
                      {request.rental_start_date ? formatEST(request.rental_start_date, 'MMM d, yyyy') : 'Date TBD'}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* JotForm Status */}
                      <Badge className={`text-[10px] ${
                        jotformInfo.color === 'emerald' ? 'bg-emerald-50 text-emerald-700' : 
                        jotformInfo.color === 'blue' ? 'bg-blue-50 text-blue-700' : 
                        'bg-slate-100 text-slate-600'
                      }`}>
                        <FileSignature className="w-3 h-3 mr-1" />
                        {jotformInfo.label}
                      </Badge>
                      {request.has_payment_method ? (
                        <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">
                          <CreditCard className="w-3 h-3 mr-1" />Paid
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-600 text-[10px]">No Payment</Badge>
                      )}
                    </div>
                  </div>
                  
                  {request.notes && (
                    <div className="mb-3 p-2 bg-slate-50 rounded-md">
                      <p className="text-xs text-slate-600">
                        <span className="font-medium text-slate-500">Note: </span>
                        {request.notes}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    {/* Status Change Dropdown */}
                    <Select 
                      value={['requested', 'confirmation pending', 'alternate_selected', 'approved', 'awaiting pickup', 'completed', 'cancelled'].includes(request.status) ? request.status : 'requested'} 
                      onValueChange={(newStatus) => handleStatusChange(request.request_id, newStatus)}
                    >
                      <SelectTrigger className="h-8 text-xs flex-1">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="requested">Requested</SelectItem>
                        <SelectItem value="confirmation pending">Confirmation Pending</SelectItem>
                        <SelectItem value="alternate_selected">Alternate Selected</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="awaiting pickup">Awaiting Pickup</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Send Email Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendEmail(request);
                      }}
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                    
                    {/* More Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 px-2">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onUserClick && onUserClick(request.user_id, request.requester_name)}>
                          <User className="w-4 h-4 mr-2" />View Customer
                        </DropdownMenuItem>
                        {hasAllPoles && jotformInfo.status === 'completed' && request.has_payment_method && (
                          <DropdownMenuItem onClick={() => handleCreateRental(request)}>
                            <Package className="w-4 h-4 mr-2" />Create Rental
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={async () => {
                            try {
                              const response = await axios.get(`${API}/pole-requests/${request.request_id}/jotform-url`);
                              if (response.data.success && response.data.url) {
                                try {
                                  await navigator.clipboard.writeText(response.data.url);
                                  toast.success("JotForm link copied!");
                                } catch (clipErr) {
                                  toast.success(
                                    <div className="text-xs">
                                      <p className="font-medium mb-1">JotForm Link:</p>
                                      <p className="break-all select-all">{response.data.url}</p>
                                    </div>,
                                    { duration: 15000 }
                                  );
                                }
                              } else {
                                toast.error("No URL returned");
                              }
                            } catch (e) {
                              console.error("JotForm URL error:", e);
                              toast.error(e.response?.data?.detail || e.message || "Failed to generate JotForm link");
                            }
                          }}
                        >
                          <FileSignature className="w-4 h-4 mr-2" />Copy JotForm Link
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API}/users/${request.user_id}/payment-method-link`);
                              if (response.data.success && response.data.url) {
                                await navigator.clipboard.writeText(response.data.url);
                                toast.success("Payment link copied!");
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed to generate payment link");
                            }
                          }}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />Copy Payment Link
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="w-20 font-semibold whitespace-nowrap">ID</TableHead>
                    <TableHead className="font-semibold whitespace-nowrap">Requester</TableHead>
                    <TableHead className="w-[170px] font-semibold whitespace-nowrap">Status</TableHead>
                    <TableHead className="w-16 text-center font-semibold whitespace-nowrap">Poles</TableHead>
                    <TableHead className="w-24 font-semibold whitespace-nowrap">Dates</TableHead>
                    <TableHead className="w-[150px] font-semibold whitespace-nowrap">Notes</TableHead>
                    <TableHead className="w-28 font-semibold whitespace-nowrap">JotForm</TableHead>
                    <TableHead className="w-24 font-semibold whitespace-nowrap">Payment</TableHead>
                    <TableHead className="w-28 font-semibold whitespace-nowrap">Availability</TableHead>
                    <TableHead className="w-32 font-semibold whitespace-nowrap">Created</TableHead>
                    <TableHead className="w-24 font-semibold whitespace-nowrap">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map(request => (
                    <PoleRequestTableRow
                      key={request.request_id}
                      request={request}
                      onStatusChange={handleStatusChange}
                      onSendEmail={handleSendEmail}
                      onOrderPole={handleOrderPole}
                      onUserClick={onUserClick}
                      onCreateRental={handleCreateRental}
                      onEdit={() => fetchRequests(true)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ==================== USERS TAB ====================

export default PoleRequestsTab;
